import { imageUrl } from '../utils/imageUrl.js';
import { imageFrameStyle, imageFrameBox } from '../utils/imageFrame.js';
import ReferenceCurrency from '../components/ReferenceCurrency.jsx';
// Marketing home: learning outcomes, AI walkthroughs, glossary preview, packs,
// bilingual parent guidance and teacher trust. Existing access/payment routes stay intact.
// Known old copy is upgraded at render time; custom Site Text edits remain editable.
import React, { useState } from 'react';
import { usePageSeo } from '../components/usePageSeo.js';
import { CIE_NOTE, homeAbout, homeTagline, homeRole, LANGUAGE_HEADLINE, STUDENT_ACHIEVEMENTS } from '../components/home/marketingCopy.js';
import { useNavigate } from 'react-router-dom';
import { parseYouTubeId } from '../components/YouTubeEmbed.jsx';
import {
    DEFAULT_FEATURES, DEFAULT_SITE_TEXT,
    DEFAULT_CONTACT_LINKS,
    FEATURE_ICON_KEYS
} from '../config/siteText.js';
import { getContactIcon, getFeatureIcon } from '../utils/iconResolvers.js';
import { PS_EYEBROW, PS_PROOF_LINE } from '../config/personalStatement.js';
import HomeAiPreview from '../components/HomeAiPreview.jsx';
import HeroPresenter from '../components/home/HeroPresenter.jsx';
import PackCoverCard from '../components/home/PackCoverCard.jsx';
import { presenterState, visiblePhotos, visibleTestimonials } from '../components/home/homeSections.js';
import { homeHero, LEARNING_STEPS, homeFeatures } from '../components/home/homeExperience.js';
import TerminologyPreview from '../components/home/TerminologyPreview.jsx';
import ParentsSection from '../components/home/ParentsSection.jsx';
import TeachingNotes from '../components/home/TeachingNotes.jsx';
import { track } from '../analytics/analytics.js';
import { freeLessonOffer } from '../components/home/proofStrip.js';
import PackFinder from '../components/home/PackFinder.jsx';
import FaqList from '../components/home/FaqList.jsx';
import { resolveFaq } from '../components/home/faq.js';

const CARD = 'rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)]';

// 4a: section headings are ordinary sentence-case headings with a hairline. The old
// tiny-eyebrow-inside-a-divider pattern is gone -- it made every section shout the
// same way, so none of them led.
function SectionHeading({ id, children, lede }) {
    return (
        <div className="mb-8">
            {/* scroll-mt: the Navbar is position:fixed, so an anchor scrolled to the top of the
                viewport would land UNDER it (the wizard's "See all packs" targets #study-packs). */}
            <h2 id={id} className="font-display text-book-text text-xl md:text-2xl font-bold leading-tight scroll-mt-24">{children}</h2>
            <div className="h-px bg-book-line mt-3" />
            {lede && <p className="font-serif text-book-muted text-base leading-relaxed mt-4 max-w-2xl">{lede}</p>}
        </div>
    );
}

export const HomePage = ({ state }) => {
    const navigate = useNavigate();
    const [showAllPacks, setShowAllPacks] = useState(false);
    const siteText = { heroGallery: DEFAULT_SITE_TEXT.heroGallery, ...(state.siteText || {}) };
    usePageSeo(siteText, 'home');

    // Personal Statement section copy: Site Text overrides (shared with the
    // landing page) with the shipped constants as fallbacks (blank -> shipped).
    const psCopy = siteText.personalStatement || {};
    const psPick = (v, fallback) => (String(v == null ? '' : v).trim() || fallback);

    const presenter = presenterState(siteText, parseYouTubeId);
    const hero = homeHero(siteText);
    const about = homeAbout(siteText);
    const features = homeFeatures(siteText.features || DEFAULT_FEATURES).map((feat, index) => ({ feat, index })).filter(x => x.feat);
    // Keep the owner's exact AI wording in a prominent card, retaining Site Text edits.
    const partnerFeature = features.find(x => x.feat.title === 'AI Study Partner') || { feat: DEFAULT_FEATURES[4], index: 4 };
    const leadFeatures = [
        { feat: { title: 'AI Explain', text: 'Understand difficult passages in your preferred language, alongside the English original.' }, index: 0 },
        partnerFeature,
        { feat: { title: 'AI Essay Feedback', text: LEARNING_STEPS[2].text }, index: 5 },
    ];
    const restFeatures = [1, 0, 2, 3].map(i => features.find(x => x.index === i)).filter(Boolean).concat(features.filter(x => x.index > 5));
    const notesFeature = features.find(x => x.index === 5)?.feat;
    const photos = visiblePhotos(siteText);
    const testimonials = visibleTestimonials(siteText);
    const signedIn = !!state.user;
    // 4b: the free-lesson CTA -- its destination, its words, and whether it is honest
    // to repeat it. With no published free item the label changes and `repeat` goes
    // false, so the page never promises a free lesson it cannot deliver.
    const offer = freeLessonOffer(state.packs, signedIn);
    const faq = resolveFaq(siteText);
    const seeAllPacks = () => {
        setShowAllPacks(true);
        const el = document.getElementById('study-packs');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="page-fade-in home-root">
            {/* <main> landmark (home-seo slice): wraps the page content sections;
                the footer stays outside. Semantic only -- no layout change. */}
            <main>
            {/* ================= HERO ================= */}
            {/* .hero-bg is already the page colour plus one soft accent radial (1a);
                4a removed the inline book-pattern SVG that sat on top of it. */}
            <section className="hero-bg">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-20">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <div>
                            <div className="font-display text-book-muted text-xs font-semibold mb-3">
                                {homeTagline(siteText.heroTagline)}
                            </div>
                            <h1 className="font-display text-book-text text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                                {hero.title}
                            </h1>
                            <p className="font-serif text-book-accent text-xl mt-3 mb-5 sig-tagline sig-tagline--hero">
                                {siteText.heroSubtitle}
                            </p>
                            <p className="font-display text-book-accent text-base font-semibold mb-3">{LANGUAGE_HEADLINE}</p>
                            {/* Keep the complete value proposition readable on phones. */}
                            <p className="font-serif text-book-muted text-base md:text-lg leading-relaxed mb-7 max-w-xl">
                                {hero.description}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                                <button onClick={() => { track('hero_lesson'); navigate(offer.href); }}
                                    disabled={!signedIn && state.packsLoading}
                                    className="px-6 py-3 rounded-full bg-book-accent text-book-pageDark font-display text-sm font-semibold hover:bg-book-accentLight transition-colors">
                                    {!signedIn && state.packsLoading ? 'Finding a free lesson…' : signedIn ? siteText.heroBtnLoggedIn : offer.label}
                                </button>
                                <button onClick={() => { track('ai_demo_open'); document.getElementById('ai-preview').scrollIntoView({ behavior: 'smooth' }); }}
                                    className="font-display text-book-accent text-sm font-semibold hover:underline">
                                    See how AI feedback works
                                </button>
                                <a href="#for-parents" className="px-3 py-3 font-display text-book-accent text-sm font-semibold hover:underline">For Parents</a>
                            </div>
                            {signedIn && <p className="font-serif text-book-muted text-xs mt-3">Open your library to read free lessons and any packs you have unlocked.</p>}
                            {!signedIn && offer.free && <p className="font-serif text-book-muted text-xs mt-3">Read a real lesson. No account or payment needed.</p>}
                        </div>
                        {/* Danielle opens the page: video, portrait, or credentials --
                            whichever she has. Never an empty slot. */}
                        <div className="mt-2 lg:mt-0 space-y-4">
                            <div className={CARD + ' p-5 md:p-6'}>
                                <p className="font-display text-book-accent text-xs font-semibold mb-4">Your next study session</p>
                                <ol className="space-y-4">{['Read a lesson with Danielle’s guidance', 'Ask your AI Study Partner when you get stuck', 'Practise an answer and get focused feedback', 'Redraft with a clear next step'].map((step, i) => <li key={step} className="flex gap-3 items-start"><span className="font-display text-book-accent text-sm font-bold">0{i + 1}</span><span className="font-serif text-book-text text-sm leading-relaxed">{step}</span></li>)}</ol>
                            </div>
                            <HeroPresenter state={presenter} siteText={siteText} />
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ FIND YOUR PACK (home-packfinder) ============ */}
            {/* Deterministic, Site-Text-driven, no AI and no network call. Renders
                nothing when switched off or left without a question or an outcome. */}

            {/* ============ WHAT CHANGES (the first three features) ============ */}
            {leadFeatures.length > 0 && (
                <section className="py-12 md:py-16 border-t border-book-line">
                    <div className="max-w-6xl mx-auto px-4 md:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {leadFeatures.map(({ feat, index }) => {
                                const Icon = getFeatureIcon(FEATURE_ICON_KEYS[index] || 'feather');
                                return (
                                    <div key={index} className={CARD + ' p-5'}>
                                        <Icon className="w-6 h-6 mb-3 text-book-accent" />
                                        <h3 className="font-display text-book-text text-base font-bold mb-1.5">{feat.title}</h3>
                                        <p className="font-serif text-book-muted text-sm leading-relaxed">{feat.text}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* "Feel the AI" pre-signup demo (public, pre-baked -- no live AI call). */}
            <HomeAiPreview siteText={siteText} offer={offer} />
            <TerminologyPreview state={state} />

            {/* ================= WHAT'S INSIDE ================= */}
            {restFeatures.length > 0 && (
                <section id="whats-inside" className="py-16 md:py-20 border-t border-book-line">
                    <div className="max-w-6xl mx-auto px-4 md:px-8">
                        <SectionHeading id="whats-inside-heading"
                            lede="Understand the ideas. Remember the essentials. Put them into practice.">
                            More ways to revise
                        </SectionHeading>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {restFeatures.map(({ feat, index }) => {
                                const Icon = getFeatureIcon(FEATURE_ICON_KEYS[index] || 'feather');
                                return (
                                    <div key={index} className={CARD + ' p-5'}>
                                        <Icon className="w-6 h-6 mb-3 text-book-accent" />
                                        <h3 className="font-display text-book-text text-base font-bold mb-1.5">{feat.title}</h3>
                                        <p className="font-serif text-book-muted text-sm leading-relaxed">{feat.text}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {notesFeature && <div className="max-w-6xl mx-auto px-4 md:px-8 pb-8 font-serif text-sm text-book-muted"><strong className="font-display text-book-text">{notesFeature.title}</strong> · {notesFeature.text}<p className="mt-2 text-xs">Activities and media vary by pack. Explore its contents and free preview.</p></div>}

            {/* ================= STUDY PACKS ================= */}
            <PackFinder siteText={siteText} packs={state.packs} signedIn={signedIn}
                onNavigate={navigate} onSeeAll={seeAllPacks} />
            <section className="py-16 md:py-20 border-t border-book-line">
                <div className="max-w-6xl mx-auto px-4 md:px-8">
                    <SectionHeading id="study-packs"
                        lede="Choose the part of your exam you want to work on. Preview a free lesson where available, then unlock the pack you need.">
                        The study packs
                    </SectionHeading>
                    <ReferenceCurrency />
                    <p className="mb-6 border-l-2 border-book-accent/40 pl-3 font-serif text-book-muted text-sm">{CIE_NOTE}</p>
                    <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page p-5 mb-6">
                        <h3 className="font-display text-book-text font-semibold mb-2">What your pack purchase covers</h3>
                        <ul className="font-serif text-book-muted text-sm leading-relaxed list-disc pl-5 space-y-1">
                            <li>12 months of access from unlock to the selected pack's study materials, or the named members of a bundle.</li>
                            <li>Content and practice activities vary by pack. Expand its full description and try the available sample before choosing.</li>
                            <li>AI Study Partner and AI Explain support your learning; AI Essay Feedback requires a pack with marking enabled. AI usage limits apply. One-to-one teaching and human marking are separate services.</li>
                        </ul>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(showAllPacks ? state.packs : state.packs.slice(0, 3)).map((pack) => (
                            <PackCoverCard key={pack.id} pack={pack} packs={state.packs}
                                signedIn={signedIn} onNavigate={navigate} />
                        ))}
                    </div>
                    {state.packs.length > 3 && <div className="text-center mt-6"><button aria-expanded={showAllPacks} className="rounded-full border border-book-accent/40 text-book-accent px-5 py-3" onClick={() => { setShowAllPacks(!showAllPacks); if (showAllPacks) document.getElementById('study-packs')?.scrollIntoView({ block: 'start' }); }}>{showAllPacks ? 'Show fewer packs' : 'View more study packs (' + (state.packs.length - 3) + ')'}</button></div>}
                </div>
            </section>

            {/* ================= ABOUT ================= */}
            <ParentsSection siteText={siteText} offer={offer} onNavigate={navigate} />
            <TeachingNotes />
            <section id="about" className="py-16 md:py-20 border-t border-book-line">
                <div className="max-w-6xl mx-auto px-4 md:px-8">
                    <SectionHeading>About {siteText.aboutName}</SectionHeading>
                    <div className="grid md:grid-cols-5 gap-8 items-start">
                        <div className="md:col-span-2">
                            <div style={imageFrameBox(siteText.profilePhotoUrlFrame, '3:4')} className={CARD + ' relative overflow-hidden'}>
                                {siteText.profilePhotoUrl ? (
                                    <img src={imageUrl(siteText.profilePhotoUrl)} style={imageFrameStyle(siteText.profilePhotoUrlFrame)} referrerPolicy="no-referrer" loading="lazy" decoding="async" fetchpriority="low"
                                        alt={siteText.aboutName}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-28 h-28 rounded-full bg-book-accent/10 border-2 border-book-accent flex items-center justify-center">
                                            <span className="font-display text-4xl font-bold text-book-accent">
                                                {(siteText.aboutName || 'D').charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="md:col-span-3 space-y-4">
                            <div className="font-display text-book-muted text-xs font-semibold">{homeRole(siteText.aboutRole)}</div>
                            <div className="space-y-4 font-serif text-book-text text-base md:text-lg leading-relaxed">
                                <p>{about.para1}</p>
                                <p>{about.para2}</p>
                                <p className="font-display text-book-accent text-sm font-semibold">{STUDENT_ACHIEVEMENTS}</p>
                                {about.quote && <p className="font-accent italic text-book-muted border-l-2 border-book-accent/40 pl-4">
                                    &ldquo;{about.quote}&rdquo;
                                </p>}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= PERSONAL STATEMENT ================= */}
            {/* 4a moved this BELOW the packs so exam prep is the spine of the page.
                Both CTAs still link to the PUBLIC landing page (/personal-statement),
                never the auth-gated request route, and the Site Text overrides still
                win over the shipped constants. */}
            <section id="personal-statement" className="py-16 md:py-20 border-t border-book-line">
                <div className="max-w-4xl mx-auto px-4 md:px-8">
                    <SectionHeading>{psPick(psCopy.eyebrow, PS_EYEBROW)}</SectionHeading>
                    <p className="font-display text-book-text text-2xl md:text-3xl font-bold leading-tight mb-4">
                        Applying to university? Let Danielle read your personal statement.
                    </p>
                    <p className="font-serif text-book-muted text-base leading-relaxed mb-3">
                        Line-by-line review of your UCAS or overseas application draft, or a one-to-one
                        admissions consultation to shape your course choice and your story before you write.
                    </p>
                    <p className="font-serif italic text-book-text text-base mb-7">
                        {psPick(psCopy.proofLine, PS_PROOF_LINE)}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={() => navigate('/personal-statement')}
                            className="px-5 py-3 rounded-full bg-book-accent text-book-pageDark font-display text-sm font-semibold hover:bg-book-accentLight transition-colors">
                            Request a review
                        </button>
                        <button onClick={() => navigate('/personal-statement')}
                            className="px-5 py-3 rounded-full border border-book-accent/50 text-book-accent font-display text-sm font-semibold hover:bg-book-accent/5 transition-colors">
                            Book a consultation
                        </button>
                    </div>
                </div>
            </section>

            {/* ========= TESTIMONIALS (admin-gated: enabled AND real content) ========= */}
            {testimonials.length > 0 && (
                <section id="testimonials-carousel" className="py-16 md:py-20 border-t border-book-line">
                    <div className="max-w-6xl mx-auto px-4 md:px-8">
                        <SectionHeading lede={siteText.testimonialsSubtitle}>{siteText.testimonialsTitle}</SectionHeading>
                        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'thin' }}>
                            {testimonials.map((t) => (
                                <div key={t.id} className={CARD + ' flex-shrink-0 w-80 md:w-[26rem] snap-center p-5'}>
                                    <p className="font-serif italic text-book-text text-base leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                                    <div className="flex items-center gap-3 pt-3 border-t border-book-line">
                                        {t.photoUrl ? (
                                            <div style={imageFrameBox(t.photoUrlFrame, '1:1')} className="w-11 rounded-full overflow-hidden border border-book-line flex-shrink-0">
                                                <img src={imageUrl(t.photoUrl)} style={imageFrameStyle(t.photoUrlFrame)} alt={t.studentName} loading="lazy" decoding="async" fetchpriority="low" className="w-full h-full object-cover" />
                                            </div>
                                        ) : (
                                            <div className="w-11 h-11 rounded-full bg-book-accent/10 border border-book-line flex-shrink-0" />
                                        )}
                                        <div>
                                            <div className="font-display text-book-text text-sm font-semibold">{t.studentName || 'Student'}</div>
                                            {t.studentContext && (
                                                <div className="font-serif italic text-book-muted text-xs mt-0.5">{t.studentContext}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ========= PHOTOS (admin-gated: enabled AND real content) ========= */}
            {photos.length > 0 && (
                <section id="photos-carousel" className="py-16 md:py-20 border-t border-book-line">
                    <div className="max-w-6xl mx-auto px-4 md:px-8">
                        <SectionHeading lede={siteText.photosCarouselSubtitle}>{siteText.photosCarouselTitle}</SectionHeading>
                        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'thin' }}>
                            {photos.map((photo) => (
                                <div key={photo.id} className={CARD + ' flex-shrink-0 w-72 md:w-96 snap-center overflow-hidden'}>
                                    <div style={imageFrameBox(photo.imageUrlFrame)} className="bg-book-bg overflow-hidden">
                                        <img src={imageUrl(photo.imageUrl)} style={imageFrameStyle(photo.imageUrlFrame)} referrerPolicy="no-referrer" loading="lazy" decoding="async" fetchpriority="low" alt={photo.caption || 'Classroom photo'} className="w-full h-full object-cover" />
                                    </div>
                                    {photo.caption && (
                                        <div className="p-4">
                                            <p className="font-serif italic text-book-muted text-sm leading-relaxed">{photo.caption}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ================= FAQ (home-packfinder) ================= */}
            {/* The same items as the prerendered /faq page. Admin-gated like the
                carousels: enabled AND at least one complete question. */}
            {faq.visible && (
                <section id="faq" className="py-16 md:py-20 border-t border-book-line">
                    <div className="max-w-4xl mx-auto px-4 md:px-8">
                        <SectionHeading id="faq-heading">{faq.heading}</SectionHeading>
                        <FaqList items={faq.items} idPrefix="homefaq" headingLevel={3} />
                    </div>
                </section>
            )}

            {/* ============ THE ONE REPEATED CTA (4b) ============ */}
            {/* A single "Try a free lesson" near the foot of the page, for a reader who
                scrolled the whole thing. Same target as the demo's CTA -- one promise,
                one destination. */}
            {offer.repeat && (
                <section className="py-14 md:py-16 border-t border-book-line">
                    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
                        <p className="font-display text-book-text text-xl md:text-2xl font-bold leading-tight mb-4">
                            {offer.free ? 'Start with a full lesson, free.' : 'Ready when you are.'}
                        </p>
                        <button onClick={() => navigate(offer.href)}
                            className="px-6 py-3 rounded-full bg-book-accent text-book-pageDark font-display text-sm font-semibold hover:bg-book-accentLight transition-colors">
                            {offer.label}
                        </button>
                    </div>
                </section>
            )}

            {/* ================= GET IN TOUCH ================= */}
            <section className="py-16 md:py-20 border-t border-book-line">
                <div className="max-w-4xl mx-auto px-4 md:px-8">
                    <SectionHeading lede="Questions about the pack? Interested in group tutoring or school licensing? Say hello.">
                        Get in touch
                    </SectionHeading>
                    {(() => {
                        const links = siteText.contactLinks || DEFAULT_CONTACT_LINKS;
                        const gridCols = links.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
                        return (
                            <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
                                {links.map((link, i) => {
                                    const Icon = getContactIcon(link.iconKey);
                                    return (
                                        <a key={i} href={link.url || '#'} target={(link.url || '').startsWith('http') ? '_blank' : undefined}
                                            rel={(link.url || '').startsWith('http') ? 'noopener noreferrer' : undefined}
                                            className={CARD + ' flex flex-col items-center gap-2 px-6 py-6 text-book-accent hover:bg-book-accent/5 transition-colors'}>
                                            <Icon className="w-5 h-5" />
                                            <div className="font-display text-xs font-semibold text-book-muted">{link.label}</div>
                                            <div className="font-serif text-sm break-all text-center text-book-text">{link.display}</div>
                                        </a>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </section>

            </main>

            <footer className="py-12 border-t border-book-line">
                <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
                    <div className="font-display text-book-text text-sm font-semibold mb-3">Between the Lines</div>
                    <div className="font-serif italic text-book-muted text-sm">
                        © {siteText.footerYear || '2026'} {siteText.footerCopyrightText || 'Between the Lines with Danielle. All rights reserved.'}
                    </div>
                    <div className="font-serif italic text-book-muted/70 text-xs mt-2">
                        Independent study resource. Not endorsed by Pearson Edexcel, OxfordAQA or Cambridge International. Exam-board names and trademarks belong to their respective owners.
                    </div>
                    <nav aria-label="Legal information" className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 font-display text-book-muted text-xs">
                        <a className="hover:underline" href="/legal#terms_of_service">Terms of Service</a>
                        <a className="hover:underline" href="/legal#privacy_policy">Privacy Policy</a>
                        <a className="hover:underline" href="/legal#refund_policy">Refund Policy</a>
                        <a className="hover:underline" href="/legal#ai_disclaimer">AI Disclaimer</a>
                        <button className="hover:underline" onClick={() => window.dispatchEvent(new Event('btl-cookie-choices'))}>Cookie choices</button>
                    </nav>
                </div>
            </footer>

        </div>
    );
};

export default HomePage;
